/**
 * API client for the public book-a-demo flow (`/book-demo`).
 * Every endpoint here is public — no Supabase token is attached.
 */

export type RevenueRange = 'lt1m' | '1to4m' | '4to10m' | '10to25m' | 'gt25m';

export interface DemoPrefill {
  businessName: string | null;
  /** 1–20, or null when the lead was recorded as "20+". */
  restaurantCount: number | null;
  restaurantCount20Plus: boolean;
  revenueRange: RevenueRange | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
}

export interface DemoSlot {
  /** ISO-8601 UTC start of an available meeting slot. */
  startTime: string;
}

export interface BookDemoPayload {
  businessName: string;
  restaurantCount: number | null;
  restaurantCount20Plus: boolean;
  revenueRange: RevenueRange;
  challenges: string[];
  contactName: string;
  email: string;
  phone?: string;
  locale: string;
  startTime: string;
  timezone: string;
  ref?: string;
  website?: string;
}

export interface DemoBookingConfirmation {
  booked: true;
  startTime: string;
  /** Null on the honeypot decoy response, which reports success without booking anything. */
  cancelUrl: string | null;
  rescheduleUrl: string | null;
}

/** The slot was taken between loading availability and submitting (server answers 409). */
export type BookDemoResult = DemoBookingConfirmation | { booked: false; reason: 'slot_taken' };

export interface DemoLeadPayload {
  email: string;
  businessName?: string;
  restaurantCount?: number | null;
  restaurantCount20Plus?: boolean;
  locale: string;
  ref?: string;
  website?: string;
}

/**
 * Prefill for a super-admin-generated link. An unknown or expired ref resolves to
 * null so the flow silently falls back to blank (spec §4.3).
 */
export async function getPrefill(ref: string): Promise<DemoPrefill | null> {
  const res = await fetch(`/api/demo/prefill/${encodeURIComponent(ref)}`);
  if (!res.ok) throw new Error(`Failed to load prefill: ${res.statusText}`);
  const data = (await res.json()) as { prefill: DemoPrefill | null };
  return data.prefill;
}

/** Available meeting slots in [startIso, endIso). The server caps the window at 31 days. */
export async function getAvailability(startIso: string, endIso: string): Promise<DemoSlot[]> {
  const query = new URLSearchParams({ start: startIso, end: endIso });
  const res = await fetch(`/api/demo/availability?${query.toString()}`);
  if (!res.ok) throw new Error(`Failed to load availability: ${res.statusText}`);
  const data = (await res.json()) as { slots: DemoSlot[] };
  return data.slots;
}

// ── Month-window availability, with a small client cache ────────────────────
// The schedule step and the contact-step prefetch must land on the SAME cache entry, so the
// key is the calendar month rather than the exact window — `start` clamps to "now", which
// differs by seconds between the two calls. In-flight promises are cached too, so moving
// quickly through the flow cannot fire the request twice.

/** Matches the server's own ~2 minute availability cache. */
const MONTH_CACHE_TTL_MS = 2 * 60 * 1000;

const monthCache = new Map<string, { promise: Promise<DemoSlot[]>; at: number }>();

const monthKey = (monthStart: Date) => `${monthStart.getFullYear()}-${monthStart.getMonth()}`;

/**
 * Availability for one calendar month. Never asks for times already past, and stops just before
 * the next month starts so the window stays inside the 31 days the server accepts.
 */
export function getAvailabilityForMonth(monthStart: Date, options?: { force?: boolean }): Promise<DemoSlot[]> {
  const key = monthKey(monthStart);
  const cached = monthCache.get(key);
  if (!options?.force && cached && Date.now() - cached.at < MONTH_CACHE_TTL_MS) return cached.promise;

  const now = new Date();
  const start = monthStart.getTime() < now.getTime() ? now : monthStart;
  const end = new Date(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1).getTime() - 1);

  const promise = getAvailability(start.toISOString(), end.toISOString());
  monthCache.set(key, { promise, at: Date.now() });
  // A failed fetch must not be remembered, or the retry would replay the same rejection.
  promise.catch(() => monthCache.delete(key));
  return promise;
}

/**
 * Warm the cache for a month the visitor is about to see. Fire-and-forget: a failure here is
 * silent and simply leaves the schedule step to fetch (and report) normally.
 */
export function prefetchAvailabilityForMonth(monthStart: Date): void {
  getAvailabilityForMonth(monthStart).catch(() => undefined);
}

export async function bookDemo(payload: BookDemoPayload): Promise<BookDemoResult> {
  const res = await fetch('/api/demo/book', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (res.status === 409) return { booked: false, reason: 'slot_taken' };
  if (!res.ok) throw new Error(`Failed to book demo: ${res.statusText}`);

  return (await res.json()) as DemoBookingConfirmation;
}

/** Below-threshold capture: stores the lead without booking anything. */
export async function submitDemoLead(payload: DemoLeadPayload): Promise<void> {
  const res = await fetch('/api/demo/lead', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to submit lead: ${res.statusText}`);
}
